import { useState } from 'react';
import tw from 'twin.macro';

import { ChevronDownIcon, FaceFrownIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import Accordion from '@/components/Accordion';
import AccordionSkeleton from '@/components/AccordionSkeleton';
import QuestionDetails from '@/components/BackofficeQuestionDetails';
import HighlightContainer from '@/components/HighlightContainer';
import IntervieweeCard from '@/components/IntervieweeCard';
import IntervieweeCardSkeleton from '@/components/IntervieweeCardSkeleton';
import Snackbar, { SnackbarVariants } from '@/components/Snackbar';
import Video from '@/components/Video';
import { IntervieweeVideoContainer } from '@/containers';
import TranscribeContainer from '@/containers/GenerativeAI/TranscribeContainer';
import { useGetQuestionSnapshotBySessionIdQuery, useGetSessionQuery } from '@/domains/session';
import { useCreateSessionViewTokenMutation } from '@/libs/graphql';
import { convertIntervieweeInfo } from '@/utils/convertIntervieweeInfo';

const Interviewee = () => {
  const params = useParams();
  const { t } = useTranslation();
  const sessionId = params.intervieweeId ?? '';
  const {
    data: sessionData,
    loading: sessionLoading,
    error: sessionError,
    refetch: refetchSession,
  } = useGetSessionQuery({
    variables: { sessionId },
    fetchPolicy: 'network-only',
  });

  const intervieweeDetails = sessionData?.getSession;

  const {
    data: questionSnapshotData,
    loading: questionSnapshotLoading,
    error: questionSnapshotError,
  } = useGetQuestionSnapshotBySessionIdQuery({
    variables: { sessionId },
    fetchPolicy: 'network-only',
    skip: !sessionId,
  });

  const [
    refreshToken,
    { data: refreshSessionData, loading: refreshTokenLoading, error: refreshTokenError },
  ] = useCreateSessionViewTokenMutation({});

  const questionSnapshot = questionSnapshotData?.getQuestionSnapshotBySessionId ?? [];

  const handleRequestSessionAccessToken = async () => {
    await refreshToken({ variables: { createSessionViewTokenInput: { sessionId } } });
    await refetchSession();
  };

  const warningBox = t('sessionViewer.noticeBox.label');
  const warningBoxLevel = t('sessionViewer.noticeBox.variant') as SnackbarVariants;

  const intervieweeInfo = convertIntervieweeInfo(intervieweeDetails);

  return (
    <div tw="m-auto flex min-h-screen flex-col space-y-5 px-4 py-6 lg:(px-20 py-14)">
      <h3>รายละเอียดการสัมภาษณ์</h3>
      <div tw="sticky top-0">
        <Snackbar text={warningBox} variant={warningBoxLevel} />
      </div>
      <div tw="flex flex-col gap-2">
        <h5>ผู้สมัคร</h5>
        {sessionLoading ? (
          <IntervieweeCardSkeleton />
        ) : (
          <IntervieweeCard
            {...intervieweeInfo}
            onRequestSessionAccessToken={handleRequestSessionAccessToken}
            requestSessionAccessTokenLoading={refreshTokenLoading}
          />
        )}
      </div>
      <div>
        <h5 tw="mb-2">วิดีโอการสัมภาษณ์</h5>
        {questionSnapshotLoading ? (
          <AccordionSkeleton lines={8} />
        ) : (
          <>
            {Array.isArray(questionSnapshot) && questionSnapshot.length === 0 ? (
              <div tw="flex h-full w-full flex-col items-center justify-center gap-2 rounded-2xl border bg-white p-14">
                <FaceFrownIcon tw="h-6 w-6" />
                <h5>ไม่พบการสัมภาษณ์</h5>
              </div>
            ) : (
              questionSnapshot.map((snapshot, index) => {
                return (
                  <Accordion
                    index={index}
                    key={`Accordion.${index}`}
                    summary={<QuestionDetails number={index + 1} title={snapshot.title} />}
                    detail={
                      <div tw="grid gap-4 w-full lg:grid-cols-2 grid-cols-1">
                        <IntervieweeVideoContainer
                          controls
                          src={snapshot.url ?? undefined}
                          fullWidth
                        />
                        <div tw="grid gap-2">
                          <TranscribeContainer
                            transcribedText={snapshot.transcribedText ?? ''}
                            jobRole={intervieweeInfo.position}
                            question={snapshot.title}
                            transcribedData={snapshot.processedTranscribedText}
                          />
                          {/* <div tw="border border-gray-200 rounded-2xl text-xl font-jamjuree w-full p-4 overflow-scroll flex flex-col gap-3">{snapshot.transcribedText}</div> */}
                        </div>
                      </div>
                    }
                  />
                );
              })
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Interviewee;
